import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FAQ_ITEMS } from '@/data/faqItems';
import seoMeta from '@/data/seo-meta.json';
import geoLanding from '@/data/geo-landing.json';

/**
 * SEOHead — gestion des balises meta, canonical, hreflang et JSON-LD côté client.
 *
 * Source de vérité partagée : src/data/seo-meta.json et src/data/geo-landing.json,
 * les mêmes fichiers qu'utilise scripts/generate-static-seo.js pour produire le HTML
 * statique servi aux moteurs. Ce composant garantit que les navigations SPA affichent
 * exactement les mêmes titres, descriptions et schémas que le HTML prérendu.
 *
 * Le JSON-LD global (Organization + LocalBusiness Paris / La Marsa + WebSite) vit dans
 * public/index.html et n'est jamais dupliqué ici — SEOHead n'émet que les schémas
 * propres à la page (WebPage, Service, FAQPage, BreadcrumbList).
 */

const SITE_URL = seoMeta.siteUrl;
const SITE_NAME = seoMeta.siteName;
const DEFAULT_IMAGE = seoMeta.defaultImage;

/** Compléments purement client (image OG dédiée, serviceType, type OpenGraph). */
const PAGE_EXTRAS = {
  '/': { type: 'website' },
  '/agence-automatisation-ia-france': { type: 'website' },
  '/agence-automatisation-ia-tunisie': { type: 'website' },
  '/services/restaurant': {
    type: 'website',
    image: `${SITE_URL}/images/restaurant.webp`,
    serviceType: 'Automatisation des opérations de restauration',
  },
  '/services/medical': {
    type: 'website',
    image: `${SITE_URL}/images/medical.webp`,
    serviceType: 'Automatisation administrative santé',
  },
  '/services/retail': {
    type: 'website',
    image: `${SITE_URL}/images/retail.webp`,
    serviceType: 'Automatisation des opérations retail',
  },
  '/mentions-legales': { type: 'article' },
  '/politique-confidentialite': { type: 'article' },
  '/cgv': { type: 'article' },
};

function normalizePath(pathname) {
  if (!pathname) return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

function upsertMeta(selector, attributes, content) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => tag.setAttribute(key, value));
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertLink(selector, attrs) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('link');
    document.head.appendChild(tag);
  }
  Object.entries(attrs).forEach(([key, value]) => tag.setAttribute(key, value));
}

function faqSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question || item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.answer || item.a },
    })),
  };
}

function buildSchemas(pathname, page) {
  const pageUrl = `${SITE_URL}${pathname === '/' ? '/' : pathname}`;
  const schemas = [];

  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: page.title,
    description: page.description,
    url: pageUrl,
    inLanguage: 'fr',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
  });

  if (pathname === '/') {
    schemas.push(faqSchema(FAQ_ITEMS));
  }

  if (page.geoKey && geoLanding[page.geoKey]) {
    schemas.push(faqSchema(geoLanding[page.geoKey].faq));
  }

  if (page.serviceName) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: page.serviceName,
      description: page.description,
      serviceType: page.serviceType || 'Automatisation par intelligence artificielle',
      provider: { '@id': `${SITE_URL}/#organization` },
      areaServed: page.serviceAreaName
        ? { '@type': 'Country', name: page.serviceAreaName }
        : [
            { '@type': 'Country', name: 'France' },
            { '@type': 'Country', name: 'Tunisie' },
          ],
      url: pageUrl,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'EUR',
        description: 'Audit gratuit sans engagement',
        url: `${SITE_URL}/#audit`,
      },
    });
  }

  if (page.breadcrumb && page.breadcrumb.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: page.breadcrumb.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: `${SITE_URL}${item.path === '/' ? '/' : item.path}`,
      })),
    });
  }

  return schemas;
}

export default function SEOHead() {
  const location = useLocation();

  useEffect(() => {
    const pathname = normalizePath(location.pathname);
    const baseConfig = seoMeta.routes[pathname];
    const isKnownPage = !!baseConfig;
    const page = {
      ...(baseConfig || {
        title: 'Page introuvable | FastTask',
        description:
          "La page recherchée n'existe pas. Découvrez FastTask, agence d'automatisation IA en France et en Tunisie : nous automatisons vos tâches répétitives pour gagner du temps et réduire vos coûts.",
      }),
      ...(PAGE_EXTRAS[pathname] || {}),
    };
    const canonical = `${SITE_URL}${pathname === '/' ? '/' : pathname}`;

    document.documentElement.setAttribute('lang', 'fr');
    document.title = page.title;

    upsertMeta('meta[name="description"]', { name: 'description' }, page.description);
    upsertMeta(
      'meta[name="robots"]',
      { name: 'robots' },
      isKnownPage
        ? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
        : 'noindex,nofollow'
    );

    upsertMeta('meta[property="og:locale"]', { property: 'og:locale' }, 'fr_FR');
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, page.type || 'website');
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, SITE_NAME);
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, page.title);
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, page.description);
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, canonical);
    upsertMeta('meta[property="og:image"]', { property: 'og:image' }, page.image || DEFAULT_IMAGE);
    upsertMeta('meta[property="og:image:width"]', { property: 'og:image:width' }, '1200');
    upsertMeta('meta[property="og:image:height"]', { property: 'og:image:height' }, '630');
    upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt' }, page.title);

    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, page.title);
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, page.description);
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, page.image || DEFAULT_IMAGE);

    // hreflang : on purge TOUTES les balises existantes (prérendu statique + injections
    // précédentes) à chaque navigation, puis on réémet le jeu correct pour la page. Les
    // pages géo déclarent un cluster réciproque fr-FR / fr-TN dans seo-meta.json
    // (baseConfig.alternates) ; les autres retombent sur fr + x-default auto-référencés.
    document.head
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((node) => node.remove());

    if (isKnownPage) {
      upsertLink('link[rel="canonical"]', { rel: 'canonical', href: canonical });
      const alternates = baseConfig.alternates || [
        { hreflang: 'fr', path: pathname },
        { hreflang: 'x-default', path: pathname },
      ];
      alternates.forEach((alt) => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', alt.hreflang);
        link.setAttribute('href', `${SITE_URL}${alt.path === '/' ? '/' : alt.path}`);
        document.head.appendChild(link);
      });
    } else {
      document.head.querySelector('link[rel="canonical"]')?.remove();
    }

    // Purge des schémas de la page précédente : ceux injectés dynamiquement ici, et ceux
    // du HTML statique prérendu (marqués data-seo-static="page" par generate-static-seo).
    document
      .querySelectorAll(
        'script[type="application/ld+json"][data-seo-dynamic="true"], script[type="application/ld+json"][data-seo-static="page"]'
      )
      .forEach((node) => node.remove());

    if (isKnownPage) {
      const schemas = buildSchemas(pathname, page);
      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-dynamic', 'true');
        script.text = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }
  }, [location.pathname]);

  return null;
}
